import { Navigate, generatePath, useParams } from 'react-router-dom';

import { usePartnersReadOffer } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import LoadErrorState from '@/components/LoadErrorState';
import OfferEditor from '@/domain/offers/components/OfferEditor';
import OfferFormSkeleton from '@/domain/offers/components/OfferFormSkeleton';
import { isOfferEditable } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

/** `/offers/new` and `/offers/:offerId/edit` (P18·b). An offer in review or decided opens its detail. */
const OfferForm = ({ partner }: Props) => {
  const t = useTranslate('domain.offers.OfferForm');
  const { offerId } = useParams<{ offerId: string }>();
  const { data: offer, isLoading, isError, refetch } = usePartnersReadOffer(offerId);

  if (!offerId) return <OfferEditor partner={partner} />;
  if (isLoading) return <OfferFormSkeleton />;
  if (isError || !offer) {
    return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
  }
  if (!isOfferEditable(offer)) {
    return <Navigate to={generatePath(ScreenRoutes.OFFER_DETAIL, { offerId })} replace />;
  }

  // Keyed by offer: a refetch after saving must not keep the previous form state.
  return <OfferEditor key={offer.updatedAt} partner={partner} offer={offer} />;
};

export default OfferForm;
